import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  isValidPhoneNumber
} from 'libphonenumber-js';

// Export getCountryCallingCode for use in other components
export { getCountryCallingCode };

// Auto-detect country code from phone number
export const detectCountryFromPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.length < 3) return null;

  try {
    // Get all available countries
    const allCountries = getCountries();

    // Enhanced priority list - countries with most users and common patterns
    const priorityCountries = [
      'IN',
      'US',
      'CN',
      'BR',
      'RU',
      'JP',
      'DE',
      'GB',
      'FR',
      'IT', // Top 10 by population
      'CA',
      'AU',
      'MX',
      'ES',
      'KR',
      'SA',
      'EG',
      'NG',
      'ZA',
      'TR', // Next 10
      'ID',
      'TH',
      'VN',
      'MY',
      'PH',
      'SG',
      'AE',
      'IL',
      'NL',
      'BE' // More common ones
    ];

    // First try priority countries for faster detection
    for (const country of priorityCountries) {
      try {
        const fullNumber = `+${getCountryCallingCode(country)}${phoneNumber}`;
        if (isValidPhoneNumber(fullNumber)) {
          console.log(`Auto-detected country: ${country} for phone number: ${phoneNumber}`);
          return country;
        }
      } catch (error) {
        continue;
      }
    }

    // If no match found with priority countries, try all countries systematically
    // Group countries by calling code length for more efficient detection
    const countriesByLength = {};

    allCountries.forEach((country) => {
      try {
        const callingCode = getCountryCallingCode(country);
        const length = callingCode.length;
        if (!countriesByLength[length]) {
          countriesByLength[length] = [];
        }
        countriesByLength[length].push(country);
      } catch (error) {
        // Skip countries with invalid calling codes
      }
    });

    // Try countries with shorter calling codes first (more likely to match)
    const sortedLengths = Object.keys(countriesByLength).sort((a, b) => parseInt(a) - parseInt(b));

    for (const length of sortedLengths) {
      const countries = countriesByLength[length];
      for (const country of countries) {
        try {
          const fullNumber = `+${getCountryCallingCode(country)}${phoneNumber}`;
          if (isValidPhoneNumber(fullNumber)) {
            console.log(`Auto-detected country: ${country} for phone number: ${phoneNumber}`);
            return country;
          }
        } catch (error) {
          continue;
        }
      }
    }

    console.log(`No country detected for phone number: ${phoneNumber}`);
    return null;
  } catch (error) {
    console.log(`Error detecting country for phone number: ${phoneNumber}`, error);
    return null;
  }
};

// Get all countries with their calling codes
export const getCountryCodeOptions = () => {
  const countries = getCountries();
  return countries
    .map((country) => ({
      value: country,
      label: `+${getCountryCallingCode(country)} ${getCountryName(country)}`,
      displayValue: `+${getCountryCallingCode(country)}`, // Show the calling code (e.g., "+91")
      callingCode: getCountryCallingCode(country),
      countryName: getCountryName(country) // Add country name for sorting
    }))
    .sort((a, b) => a.countryName.localeCompare(b.countryName)); // Sort alphabetically by country name (e.g., Afghanistan, Brazil, China, India, United States)
};

// Get all available countries for debugging and verification
export const getAllAvailableCountries = () => {
  const countries = getCountries();
  return countries
    .map((country) => {
      try {
        return {
          code: country,
          name: getCountryName(country),
          callingCode: getCountryCallingCode(country),
          phoneLength: getPhoneNumberLength(country)
        };
      } catch (error) {
        return {
          code: country,
          name: getCountryName(country),
          callingCode: 'N/A',
          phoneLength: { min: 0, max: 0 },
          error: error.message
        };
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Get total count of available countries
export const getTotalCountryCount = () => {
  return getCountries().length;
};

// Get countries grouped by region for better organization
export const getCountriesByRegion = () => {
  const countries = getCountries();

  const regions = {
    Asia: [
      'IN',
      'CN',
      'JP',
      'KR',
      'TH',
      'VN',
      'MY',
      'PH',
      'SG',
      'ID',
      'BD',
      'LK',
      'NP',
      'BT',
      'MV',
      'MM',
      'LA',
      'KH',
      'MN',
      'KP',
      'TW',
      'HK',
      'MO'
    ],
    Europe: [
      'GB',
      'DE',
      'FR',
      'IT',
      'ES',
      'RU',
      'NL',
      'BE',
      'CH',
      'AT',
      'HU',
      'CZ',
      'SK',
      'PL',
      'RO',
      'BG',
      'HR',
      'SI',
      'BA',
      'RS',
      'ME',
      'MK',
      'AL',
      'GR',
      'CY',
      'MT',
      'IS',
      'NO',
      'SE',
      'FI',
      'DK',
      'EE',
      'LV',
      'LT',
      'BY',
      'UA',
      'MD',
      'GE',
      'AM',
      'AZ',
      'KZ',
      'UZ',
      'KG',
      'TJ',
      'TM',
      'AF',
      'PK'
    ],
    Americas: [
      'US',
      'CA',
      'BR',
      'MX',
      'AR',
      'CO',
      'PE',
      'VE',
      'CL',
      'EC',
      'BO',
      'PY',
      'UY',
      'GY',
      'SR',
      'GF',
      'FK',
      'GS',
      'AI',
      'AG',
      'AW',
      'BS',
      'BB',
      'BZ',
      'BM',
      'VG',
      'KY',
      'CR',
      'CU',
      'DM',
      'DO',
      'SV',
      'GD',
      'GT',
      'HT',
      'HN',
      'JM',
      'NI',
      'PA',
      'KN',
      'LC',
      'VC',
      'TT',
      'PR',
      'VI',
      'AS',
      'GU',
      'MP'
    ],
    Africa: [
      'ZA',
      'NG',
      'EG',
      'SA',
      'AE',
      'KE',
      'GH',
      'UG',
      'TZ',
      'ET',
      'DZ',
      'MA',
      'TN',
      'LY',
      'SD',
      'SO',
      'DJ',
      'ER',
      'SS',
      'CF',
      'TD',
      'CM',
      'GQ',
      'GA',
      'CG',
      'CD',
      'AO',
      'ZM',
      'ZW',
      'BW',
      'NA',
      'SZ',
      'LS',
      'MG',
      'MU',
      'SC',
      'KM',
      'MZ',
      'MW',
      'BI',
      'RW',
      'SL',
      'LR',
      'CI',
      'BF',
      'ML',
      'NE',
      'SN',
      'GN',
      'GW',
      'CV',
      'GM',
      'TG',
      'BJ',
      'MR',
      'EH',
      'ST',
      'PT',
      'IE',
      'RE',
      'YT',
      'BL',
      'MF',
      'PM',
      'SX',
      'CW',
      'BQ'
    ],
    Oceania: [
      'AU',
      'NZ',
      'FJ',
      'PG',
      'SB',
      'VU',
      'NC',
      'PF',
      'TO',
      'WS',
      'KI',
      'TV',
      'NR',
      'PW',
      'FM',
      'MH',
      'CK',
      'NU',
      'TK',
      'AQ',
      'BV',
      'IO',
      'CX',
      'CC',
      'HM',
      'NF',
      'PN',
      'SH',
      'TC',
      'WF',
      'GL',
      'FO',
      'AX',
      'GI',
      'AD',
      'MC',
      'SM',
      'VA',
      'LI'
    ]
  };

  const result = {};

  Object.keys(regions).forEach((region) => {
    result[region] = countries
      .filter((country) => regions[region].includes(country))
      .map((country) => {
        try {
          return {
            code: country,
            name: getCountryName(country),
            callingCode: getCountryCallingCode(country),
            phoneLength: getPhoneNumberLength(country)
          };
        } catch (error) {
          return {
            code: country,
            name: getCountryName(country),
            callingCode: 'N/A',
            phoneLength: { min: 0, max: 0 },
            error: error.message
          };
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  return result;
};

// Test auto-detection with sample phone numbers from different countries
export const testAutoDetection = () => {
  const testCases = [
    { phone: '9876543210', expected: 'IN' }, // India
    { phone: '5551234567', expected: 'US' }, // United States
    { phone: '7912345678', expected: 'RU' }, // Russia
    { phone: '13800138000', expected: 'CN' }, // China
    { phone: '09012345678', expected: 'JP' }, // Japan
    { phone: '015123456789', expected: 'DE' }, // Germany
    { phone: '07123456789', expected: 'GB' }, // United Kingdom
    { phone: '0123456789', expected: 'FR' }, // France
    { phone: '0123456789', expected: 'IT' }, // Italy
    { phone: '61234567890', expected: 'AU' }, // Australia
    { phone: '15551234567', expected: 'CA' }, // Canada
    { phone: '5512345678', expected: 'BR' }, // Brazil
    { phone: '5512345678', expected: 'MX' }, // Mexico
    { phone: '1012345678', expected: 'KR' }, // South Korea
    { phone: '501234567', expected: 'SA' }, // Saudi Arabia
    { phone: '1012345678', expected: 'EG' }, // Egypt
    { phone: '8012345678', expected: 'NG' }, // Nigeria
    { phone: '27123456789', expected: 'ZA' }, // South Africa
    { phone: '2123456789', expected: 'TR' }, // Turkey
    { phone: '81234567890', expected: 'ID' }, // Indonesia
    { phone: '8123456789', expected: 'TH' }, // Thailand
    { phone: '912345678', expected: 'VN' }, // Vietnam
    { phone: '123456789', expected: 'MY' }, // Malaysia
    { phone: '9123456789', expected: 'PH' }, // Philippines
    { phone: '81234567', expected: 'SG' }, // Singapore
    { phone: '501234567', expected: 'AE' }, // UAE
    { phone: '501234567', expected: 'IL' }, // Israel
    { phone: '612345678', expected: 'NL' }, // Netherlands
    { phone: '412345678', expected: 'BE' } // Belgium
  ];

  console.log('🧪 Testing Auto-Detection for All Countries...');
  console.log(`📊 Total countries available: ${getTotalCountryCount()}`);

  const results = testCases.map((testCase) => {
    const detected = detectCountryFromPhoneNumber(testCase.phone);
    const success = detected === testCase.expected;

    console.log(
      `${success ? '✅' : '❌'} ${testCase.phone} -> Expected: ${testCase.expected}, Detected: ${detected || 'None'}`
    );

    return {
      phone: testCase.phone,
      expected: testCase.expected,
      detected: detected,
      success: success
    };
  });

  const successCount = results.filter((r) => r.success).length;
  const totalCount = results.length;

  console.log(
    `\n📈 Auto-Detection Results: ${successCount}/${totalCount} successful (${Math.round((successCount / totalCount) * 100)}%)`
  );

  return results;
};

// Browser console test function - run this in browser console to test all functionality
export const runAllTests = () => {
  console.log('🚀 Running All Phone Number Utility Tests...\n');

  // Test 1: Get total country count
  console.log('1️⃣ Total Countries Available:');
  console.log(`   📊 Total: ${getTotalCountryCount()} countries\n`);

  // Test 2: Get countries by region
  console.log('2️⃣ Countries by Region:');
  const regions = getCountriesByRegion();
  Object.keys(regions).forEach((region) => {
    console.log(`   🌍 ${region}: ${regions[region].length} countries`);
  });
  console.log('');

  // Test 3: Test auto-detection
  console.log('3️⃣ Auto-Detection Test:');
  const detectionResults = testAutoDetection();

  // Test 4: Sample country codes
  console.log('\n4️⃣ Sample Country Codes:');
  const sampleCountries = ['IN', 'US', 'GB', 'DE', 'FR', 'JP', 'CN', 'AU', 'CA', 'BR'];
  sampleCountries.forEach((country) => {
    try {
      const callingCode = getCountryCallingCode(country);
      const name = getCountryName(country);
      const phoneLength = getPhoneNumberLength(country);
      console.log(
        `   ${country}: ${name} (+${callingCode}) - Phone: ${phoneLength.min}-${phoneLength.max} digits`
      );
    } catch (error) {
      console.log(`   ${country}: Error - ${error.message}`);
    }
  });

  console.log('\n✅ All tests completed! Check the results above.');
  return {
    totalCountries: getTotalCountryCount(),
    regions: regions,
    detectionResults: detectionResults
  };
};

// Get country name from country code
const getCountryName = (countryCode) => {
  const countryNames = {
    IN: 'India',
    US: 'United States',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    DE: 'Germany',
    FR: 'France',
    IT: 'Italy',
    ES: 'Spain',
    BR: 'Brazil',
    MX: 'Mexico',
    JP: 'Japan',
    CN: 'China',
    RU: 'Russia',
    ZA: 'South Africa',
    AE: 'UAE',
    SA: 'Saudi Arabia',
    SG: 'Singapore',
    MY: 'Malaysia',
    TH: 'Thailand',
    VN: 'Vietnam',
    PH: 'Philippines',
    ID: 'Indonesia',
    TR: 'Turkey',
    EG: 'Egypt',
    NG: 'Nigeria',
    KE: 'Kenya',
    GH: 'Ghana',
    UG: 'Uganda',
    TZ: 'Tanzania',
    ET: 'Ethiopia',
    DZ: 'Algeria',
    MA: 'Morocco',
    TN: 'Tunisia',
    LY: 'Libya',
    SD: 'Sudan',
    SO: 'Somalia',
    DJ: 'Djibouti',
    ER: 'Eritrea',
    SS: 'South Sudan',
    CF: 'Central African Republic',
    TD: 'Chad',
    CM: 'Cameroon',
    GQ: 'Equatorial Guinea',
    GA: 'Gabon',
    CG: 'Republic of the Congo',
    CD: 'Democratic Republic of the Congo',
    AO: 'Angola',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
    BW: 'Botswana',
    NA: 'Namibia',
    SZ: 'Eswatini',
    LS: 'Lesotho',
    MG: 'Madagascar',
    MU: 'Mauritius',
    SC: 'Seychelles',
    KM: 'Comoros',
    MZ: 'Mozambique',
    MW: 'Malawi',
    BI: 'Burundi',
    RW: 'Rwanda',
    SL: 'Sierra Leone',
    LR: 'Liberia',
    CI: 'Ivory Coast',
    BF: 'Burkina Faso',
    ML: 'Mali',
    NE: 'Niger',
    SN: 'Senegal',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    CV: 'Cape Verde',
    GM: 'Gambia',
    TG: 'Togo',
    BJ: 'Benin',
    MR: 'Mauritania',
    EH: 'Western Sahara',
    ST: 'Sao Tome and Principe',
    PT: 'Portugal',
    IE: 'Ireland',
    NL: 'Netherlands',
    BE: 'Belgium',
    LU: 'Luxembourg',
    CH: 'Switzerland',
    AT: 'Austria',
    HU: 'Hungary',
    CZ: 'Czech Republic',
    SK: 'Slovakia',
    PL: 'Poland',
    RO: 'Romania',
    BG: 'Bulgaria',
    HR: 'Croatia',
    SI: 'Slovenia',
    BA: 'Bosnia and Herzegovina',
    RS: 'Serbia',
    ME: 'Montenegro',
    MK: 'North Macedonia',
    AL: 'Albania',
    GR: 'Greece',
    CY: 'Cyprus',
    MT: 'Malta',
    IS: 'Iceland',
    NO: 'Norway',
    SE: 'Sweden',
    FI: 'Finland',
    DK: 'Denmark',
    EE: 'Estonia',
    LV: 'Latvia',
    LT: 'Lithuania',
    BY: 'Belarus',
    UA: 'Ukraine',
    MD: 'Moldova',
    GE: 'Georgia',
    AM: 'Armenia',
    AZ: 'Azerbaijan',
    KZ: 'Kazakhstan',
    UZ: 'Uzbekistan',
    KG: 'Kyrgyzstan',
    TJ: 'Tajikistan',
    TM: 'Turkmenistan',
    AF: 'Afghanistan',
    PK: 'Pakistan',
    BD: 'Bangladesh',
    LK: 'Sri Lanka',
    NP: 'Nepal',
    BT: 'Bhutan',
    MV: 'Maldives',
    MM: 'Myanmar',
    LA: 'Laos',
    KH: 'Cambodia',
    MN: 'Mongolia',
    KP: 'North Korea',
    KR: 'South Korea',
    TW: 'Taiwan',
    HK: 'Hong Kong',
    MO: 'Macau',
    NZ: 'New Zealand',
    FJ: 'Fiji',
    PG: 'Papua New Guinea',
    SB: 'Solomon Islands',
    VU: 'Vanuatu',
    NC: 'New Caledonia',
    PF: 'French Polynesia',
    TO: 'Tonga',
    WS: 'Samoa',
    KI: 'Kiribati',
    TV: 'Tuvalu',
    NR: 'Nauru',
    PW: 'Palau',
    FM: 'Micronesia',
    MH: 'Marshall Islands',
    CK: 'Cook Islands',
    NU: 'Niue',
    TK: 'Tokelau',
    AS: 'American Samoa',
    GU: 'Guam',
    MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico',
    VI: 'U.S. Virgin Islands',
    AI: 'Anguilla',
    AG: 'Antigua and Barbuda',
    AW: 'Aruba',
    BS: 'Bahamas',
    BB: 'Barbados',
    BZ: 'Belize',
    BM: 'Bermuda',
    VG: 'British Virgin Islands',
    KY: 'Cayman Islands',
    CR: 'Costa Rica',
    CU: 'Cuba',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    SV: 'El Salvador',
    GD: 'Grenada',
    GT: 'Guatemala',
    HT: 'Haiti',
    HN: 'Honduras',
    JM: 'Jamaica',
    NI: 'Nicaragua',
    PA: 'Panama',
    PY: 'Paraguay',
    PE: 'Peru',
    KN: 'Saint Kitts and Nevis',
    LC: 'Saint Lucia',
    VC: 'Saint Vincent and the Grenadines',
    SR: 'Suriname',
    TT: 'Trinidad and Tobago',
    UY: 'Uruguay',
    VE: 'Venezuela',
    GY: 'Guyana',
    EC: 'Ecuador',
    CO: 'Colombia',
    CL: 'Chile',
    BO: 'Bolivia',
    AR: 'Argentina',
    GF: 'French Guiana',
    FK: 'Falkland Islands',
    GS: 'South Georgia and the South Sandwich Islands',
    AQ: 'Antarctica',
    BV: 'Bouvet Island',
    IO: 'British Indian Ocean Territory',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    HM: 'Heard Island and McDonald Islands',
    NF: 'Norfolk Island',
    PN: 'Pitcairn',
    SH: 'Saint Helena',
    TC: 'Turks and Caicos Islands',
    WF: 'Wallis and Futuna',
    YT: 'Mayotte',
    RE: 'Réunion',
    BL: 'Saint Barthélemy',
    MF: 'Saint Martin (French part)',
    PM: 'Saint Pierre and Miquelon',
    SX: 'Sint Maarten (Dutch part)',
    CW: 'Curaçao',
    BQ: 'Bonaire, Sint Eustatius and Saba',
    GL: 'Greenland',
    FO: 'Faroe Islands',
    AX: 'Åland Islands',
    GI: 'Gibraltar',
    AD: 'Andorra',
    MC: 'Monaco',
    SM: 'San Marino',
    VA: 'Vatican City',
    LI: 'Liechtenstein',
    VAT: 'Vatican City'
  };

  return countryNames[countryCode] || countryCode;
};

// Validate phone number based on country code
export const validatePhoneNumber = (phoneNumber, countryCode) => {
  if (!phoneNumber || !countryCode) return false;

  try {
    const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneNumber}`;
    return isValidPhoneNumber(fullNumber);
  } catch (error) {
    return false;
  }
};

// Format phone number for display
export const formatPhoneNumber = (phoneNumber, countryCode) => {
  if (!phoneNumber || !countryCode) return phoneNumber;

  try {
    const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneNumber}`;
    const parsed = parsePhoneNumber(fullNumber);
    return parsed.formatInternational();
  } catch (error) {
    return phoneNumber;
  }
};

// Get phone number length for a specific country
export const getPhoneNumberLength = (countryCode) => {
  try {
    // Common phone number lengths by country
    const phoneLengths = {
      IN: { min: 10, max: 10 }, // India
      US: { min: 10, max: 10 }, // United States
      GB: { min: 10, max: 11 }, // United Kingdom
      CA: { min: 10, max: 10 }, // Canada
      AU: { min: 9, max: 9 }, // Australia
      DE: { min: 10, max: 12 }, // Germany
      FR: { min: 10, max: 10 }, // France
      IT: { min: 10, max: 10 }, // Italy
      ES: { min: 9, max: 9 }, // Spain
      BR: { min: 10, max: 11 }, // Brazil
      MX: { min: 10, max: 10 }, // Mexico
      JP: { min: 10, max: 11 }, // Japan
      CN: { min: 11, max: 11 }, // China
      RU: { min: 10, max: 10 }, // Russia
      ZA: { min: 9, max: 9 }, // South Africa
      AE: { min: 9, max: 9 }, // UAE
      SA: { min: 9, max: 9 }, // Saudi Arabia
      SG: { min: 8, max: 8 }, // Singapore
      MY: { min: 9, max: 10 }, // Malaysia
      TH: { min: 9, max: 9 }, // Thailand
      VN: { min: 9, max: 10 }, // Vietnam
      PH: { min: 10, max: 10 }, // Philippines
      ID: { min: 9, max: 12 }, // Indonesia
      TR: { min: 10, max: 10 }, // Turkey
      EG: { min: 10, max: 10 }, // Egypt
      NG: { min: 10, max: 11 }, // Nigeria
      KE: { min: 9, max: 9 }, // Kenya
      GH: { min: 9, max: 9 }, // Ghana
      UG: { min: 9, max: 9 }, // Uganda
      TZ: { min: 9, max: 9 }, // Tanzania
      ET: { min: 9, max: 9 }, // Ethiopia
      DZ: { min: 9, max: 9 }, // Algeria
      MA: { min: 9, max: 9 }, // Morocco
      TN: { min: 8, max: 8 }, // Tunisia
      LY: { min: 9, max: 9 }, // Libya
      SD: { min: 9, max: 9 }, // Sudan
      SO: { min: 8, max: 8 }, // Somalia
      DJ: { min: 8, max: 8 }, // Djibouti
      ER: { min: 7, max: 7 }, // Eritrea
      SS: { min: 9, max: 9 }, // South Sudan
      CF: { min: 8, max: 8 }, // Central African Republic
      TD: { min: 8, max: 8 }, // Chad
      CM: { min: 9, max: 9 }, // Cameroon
      GQ: { min: 9, max: 9 }, // Equatorial Guinea
      GA: { min: 8, max: 8 }, // Gabon
      CG: { min: 9, max: 9 }, // Republic of the Congo
      CD: { min: 9, max: 9 }, // Democratic Republic of the Congo
      AO: { min: 9, max: 9 }, // Angola
      ZM: { min: 9, max: 9 }, // Zambia
      ZW: { min: 9, max: 9 }, // Zimbabwe
      BW: { min: 8, max: 8 }, // Botswana
      NA: { min: 9, max: 9 }, // Namibia
      SZ: { min: 8, max: 8 }, // Eswatini
      LS: { min: 8, max: 8 }, // Lesotho
      MG: { min: 9, max: 9 }, // Madagascar
      MU: { min: 8, max: 8 }, // Mauritius
      SC: { min: 7, max: 7 }, // Seychelles
      KM: { min: 7, max: 7 }, // Comoros
      MZ: { min: 9, max: 9 }, // Mozambique
      MW: { min: 9, max: 9 }, // Malawi
      BI: { min: 8, max: 8 }, // Burundi
      RW: { min: 9, max: 9 }, // Rwanda
      SL: { min: 8, max: 8 }, // Sierra Leone
      LR: { min: 8, max: 8 }, // Liberia
      CI: { min: 10, max: 10 }, // Ivory Coast
      BF: { min: 8, max: 8 }, // Burkina Faso
      ML: { min: 8, max: 8 }, // Mali
      NE: { min: 8, max: 8 }, // Niger
      SN: { min: 9, max: 9 }, // Senegal
      GN: { min: 9, max: 9 }, // Guinea
      GW: { min: 7, max: 7 }, // Guinea-Bissau
      CV: { min: 7, max: 7 }, // Cape Verde
      GM: { min: 7, max: 7 }, // Gambia
      TG: { min: 8, max: 8 }, // Togo
      BJ: { min: 8, max: 8 }, // Benin
      MR: { min: 8, max: 8 }, // Mauritania
      EH: { min: 9, max: 9 }, // Western Sahara
      ST: { min: 7, max: 7 }, // Sao Tome and Principe
      PT: { min: 9, max: 9 }, // Portugal
      IE: { min: 9, max: 9 }, // Ireland
      NL: { min: 9, max: 9 }, // Netherlands
      BE: { min: 9, max: 9 }, // Belgium
      LU: { min: 9, max: 9 }, // Luxembourg
      CH: { min: 9, max: 9 }, // Switzerland
      AT: { min: 10, max: 13 }, // Austria
      HU: { min: 9, max: 9 }, // Hungary
      CZ: { min: 9, max: 9 }, // Czech Republic
      SK: { min: 9, max: 9 }, // Slovakia
      PL: { min: 9, max: 9 }, // Poland
      RO: { min: 9, max: 9 }, // Romania
      BG: { min: 9, max: 9 }, // Bulgaria
      HR: { min: 9, max: 9 }, // Croatia
      SI: { min: 8, max: 8 }, // Slovenia
      BA: { min: 8, max: 8 }, // Bosnia and Herzegovina
      RS: { min: 9, max: 9 }, // Serbia
      ME: { min: 8, max: 8 }, // Montenegro
      MK: { min: 8, max: 8 }, // North Macedonia
      AL: { min: 9, max: 9 }, // Albania
      GR: { min: 10, max: 10 }, // Greece
      CY: { min: 8, max: 8 }, // Cyprus
      MT: { min: 8, max: 8 }, // Malta
      IS: { min: 7, max: 7 }, // Iceland
      NO: { min: 8, max: 8 }, // Norway
      SE: { min: 9, max: 9 }, // Sweden
      FI: { min: 9, max: 9 }, // Finland
      DK: { min: 8, max: 8 }, // Denmark
      EE: { min: 8, max: 8 }, // Estonia
      LV: { min: 8, max: 8 }, // Latvia
      LT: { min: 8, max: 8 }, // Lithuania
      BY: { min: 9, max: 9 }, // Belarus
      UA: { min: 9, max: 9 }, // Ukraine
      MD: { min: 8, max: 8 }, // Moldova
      GE: { min: 9, max: 9 }, // Georgia
      AM: { min: 8, max: 8 }, // Armenia
      AZ: { min: 9, max: 9 }, // Azerbaijan
      KZ: { min: 10, max: 10 }, // Kazakhstan
      UZ: { min: 9, max: 9 }, // Uzbekistan
      KG: { min: 9, max: 9 }, // Kyrgyzstan
      TJ: { min: 9, max: 9 }, // Tajikistan
      TM: { min: 8, max: 8 }, // Turkmenistan
      AF: { min: 9, max: 9 }, // Afghanistan
      PK: { min: 10, max: 10 }, // Pakistan
      BD: { min: 10, max: 11 }, // Bangladesh
      LK: { min: 9, max: 9 }, // Sri Lanka
      NP: { min: 10, max: 10 }, // Nepal
      BT: { min: 8, max: 8 }, // Bhutan
      MV: { min: 7, max: 7 }, // Maldives
      MM: { min: 9, max: 11 }, // Myanmar
      LA: { min: 10, max: 10 }, // Laos
      KH: { min: 9, max: 9 }, // Cambodia
      MN: { min: 8, max: 8 }, // Mongolia
      KP: { min: 10, max: 10 }, // North Korea
      KR: { min: 10, max: 11 }, // South Korea
      TW: { min: 9, max: 9 }, // Taiwan
      HK: { min: 8, max: 8 }, // Hong Kong
      MO: { min: 8, max: 8 }, // Macau
      NZ: { min: 9, max: 10 }, // New Zealand
      FJ: { min: 7, max: 7 }, // Fiji
      PG: { min: 8, max: 8 }, // Papua New Guinea
      SB: { min: 7, max: 7 }, // Solomon Islands
      VU: { min: 7, max: 7 }, // Vanuatu
      NC: { min: 6, max: 6 }, // New Caledonia
      PF: { min: 8, max: 8 }, // French Polynesia
      TO: { min: 7, max: 7 }, // Tonga
      WS: { min: 7, max: 7 }, // Samoa
      KI: { min: 8, max: 8 }, // Kiribati
      TV: { min: 7, max: 7 }, // Tuvalu
      NR: { min: 7, max: 7 }, // Nauru
      PW: { min: 7, max: 7 }, // Palau
      FM: { min: 7, max: 7 }, // Micronesia
      MH: { min: 7, max: 7 }, // Marshall Islands
      CK: { min: 5, max: 5 }, // Cook Islands
      NU: { min: 4, max: 4 }, // Niue
      TK: { min: 4, max: 4 }, // Tokelau
      AS: { min: 10, max: 10 }, // American Samoa
      GU: { min: 10, max: 10 }, // Guam
      MP: { min: 10, max: 10 }, // Northern Mariana Islands
      PR: { min: 10, max: 10 }, // Puerto Rico
      VI: { min: 10, max: 10 }, // U.S. Virgin Islands
      AI: { min: 10, max: 10 }, // Anguilla
      AG: { min: 10, max: 10 }, // Antigua and Barbuda
      AW: { min: 7, max: 7 }, // Aruba
      BS: { min: 10, max: 10 }, // Bahamas
      BB: { min: 10, max: 10 }, // Barbados
      BZ: { min: 7, max: 7 }, // Belize
      BM: { min: 10, max: 10 }, // Bermuda
      VG: { min: 10, max: 10 }, // British Virgin Islands
      KY: { min: 10, max: 10 }, // Cayman Islands
      CR: { min: 8, max: 8 }, // Costa Rica
      CU: { min: 8, max: 8 }, // Cuba
      DM: { min: 10, max: 10 }, // Dominica
      DO: { min: 10, max: 10 }, // Dominican Republic
      SV: { min: 8, max: 8 }, // El Salvador
      GD: { min: 10, max: 10 }, // Grenada
      GT: { min: 8, max: 8 }, // Guatemala
      HT: { min: 8, max: 8 }, // Haiti
      HN: { min: 8, max: 8 }, // Honduras
      JM: { min: 10, max: 10 }, // Jamaica
      NI: { min: 8, max: 8 }, // Nicaragua
      PA: { min: 8, max: 8 }, // Panama
      PY: { min: 9, max: 9 }, // Paraguay
      PE: { min: 9, max: 9 }, // Peru
      KN: { min: 10, max: 10 }, // Saint Kitts and Nevis
      LC: { min: 10, max: 10 }, // Saint Lucia
      VC: { min: 10, max: 10 }, // Saint Vincent and the Grenadines
      SR: { min: 7, max: 7 }, // Suriname
      TT: { min: 10, max: 10 }, // Trinidad and Tobago
      UY: { min: 8, max: 8 }, // Uruguay
      VE: { min: 10, max: 10 }, // Venezuela
      GY: { min: 7, max: 7 }, // Guyana
      EC: { min: 9, max: 9 }, // Ecuador
      CO: { min: 10, max: 10 }, // Colombia
      CL: { min: 9, max: 9 }, // Chile
      BO: { min: 8, max: 8 }, // Bolivia
      AR: { min: 10, max: 10 }, // Argentina
      GF: { min: 9, max: 9 }, // French Guiana
      FK: { min: 5, max: 5 }, // Falkland Islands
      GS: { min: 5, max: 5 }, // South Georgia and the South Sandwich Islands
      AQ: { min: 0, max: 0 }, // Antarctica (no phone numbers)
      BV: { min: 0, max: 0 }, // Bouvet Island (no phone numbers)
      IO: { min: 7, max: 7 }, // British Indian Ocean Territory
      CX: { min: 8, max: 8 }, // Christmas Island
      CC: { min: 8, max: 8 }, // Cocos (Keeling) Islands
      HM: { min: 0, max: 0 }, // Heard Island and McDonald Islands (no phone numbers)
      NF: { min: 6, max: 6 }, // Norfolk Island
      PN: { min: 4, max: 4 }, // Pitcairn
      SH: { min: 4, max: 4 }, // Saint Helena
      TC: { min: 10, max: 10 }, // Turks and Caicos Islands
      WF: { min: 6, max: 6 }, // Wallis and Futuna
      YT: { min: 9, max: 9 }, // Mayotte
      RE: { min: 9, max: 9 }, // Réunion
      BL: { min: 9, max: 9 }, // Saint Barthélemy
      MF: { min: 9, max: 9 }, // Saint Martin (French part)
      PM: { min: 6, max: 6 }, // Saint Pierre and Miquelon
      SX: { min: 10, max: 10 }, // Sint Maarten (Dutch part)
      CW: { min: 7, max: 7 }, // Curaçao
      BQ: { min: 7, max: 7 }, // Bonaire, Sint Eustatius and Saba
      GL: { min: 6, max: 6 }, // Greenland
      FO: { min: 6, max: 6 }, // Faroe Islands
      AX: { min: 6, max: 6 }, // Åland Islands
      GI: { min: 8, max: 8 }, // Gibraltar
      AD: { min: 6, max: 6 }, // Andorra
      MC: { min: 8, max: 8 }, // Monaco
      SM: { min: 10, max: 10 }, // San Marino
      VA: { min: 10, max: 10 }, // Vatican City
      LI: { min: 7, max: 7 }, // Liechtenstein
      VAT: { min: 10, max: 10 } // Vatican City (alternative code)
    };

    return phoneLengths[countryCode] || { min: 7, max: 15 }; // Default fallback
  } catch (error) {
    return { min: 7, max: 15 }; // Default fallback
  }
};

// Clean phone number (remove non-digits)
export const cleanPhoneNumber = (phoneNumber) => {
  return phoneNumber.replace(/\D/g, '');
};

// Get default country code (India for this application)
export const getDefaultCountryCode = () => 'IN';
